/**
 * Конверт шифротексту: версія схеми й ідентифікатор алгоритму перед даними.
 *
 * Навіщо це існує ([ADR-0008](../../../../docs/adr/0008-crypto-primitives.md)):
 * зміна крипто-примітиву без версії у форматі перетворюється на міграцію
 * наосліп — старий блоб неможливо відрізнити від нового інакше, ніж спробувавши
 * розшифрувати. Два байти зараз коштують нічого; їх відсутність потім коштує
 * цілісності архіву.
 *
 * Цей модуль не шифрує. Він лише описує обгортку; самі примітиви приходять
 * реалізацією порту `CipherPort` — різною на кожній платформі ([ADR-0015]).
 */
/** Поточна версія схеми конверта. Росте, коли змінюється сама розкладка байтів. */
export const SCHEMA_VERSION = 1;
/**
 * Ідентифікатори алгоритмів. Значення **ніколи не перевикористовуються**:
 * навіть коли алгоритм виходить з ужитку, його номер лишається зайнятим,
 * бо в архіві можуть лежати старі блоби.
 */
export const Algorithm = {
    /** XChaCha20-Poly1305: нонс 24 байти окремим полем. Вміст записів. */
    XChaCha20Poly1305: 1,
    /**
     * Черга захоплення при замкненій базі: `epk ‖ конверт-1`.
     *
     * Окремого нонса на цьому рівні немає — він усередині вкладеного конверта
     * алгоритму 1, разом із міткою автентичності.
     *
     * Називався `X25519SealedBox`, поки [ADR-0011] описував механізм словами
     * libsodium. [ADR-0031] замінив механізм на X25519 з WebCrypto плюс HKDF,
     * тож ім'я «sealed box» стало неправдою. **Номер лишається 2**: значення
     * ніколи не перевикористовуються, і блоби, записані під старим ім'ям,
     * читаються тим самим кодом.
     */
    X25519Capture: 2,
    /**
     * AES-256-GCM: нонс 12 байтів, тег усередині шифротексту.
     *
     * Для ключа пристрою у вебі ([ADR-0015]). Причина не в стійкості —
     * XChaCha20 не гірший, — а в тому, що WebCrypto вміє тримати ключ
     * невитягуваним. Ключ, який ніколи не існує в JS як байти, не можна
     * вкрасти скриптом і розшифрувати ним вкрадену копію бази окремо.
     */
    AesGcm256: 3,
};
/** Довжина поля нонса для кожного алгоритму. Нуль означає, що поля немає. */
const NONCE_BYTES = {
    [Algorithm.XChaCha20Poly1305]: 24,
    [Algorithm.X25519Capture]: 0,
    [Algorithm.AesGcm256]: 12,
};
/** Версія + алгоритм. Магічних байтів немає: блоб завжди лежить у відомому контексті. */
export const HEADER_BYTES = 2;
export class MalformedEnvelopeError extends Error {
    constructor(reason) {
        super(`пошкоджений конверт шифротексту: ${reason}`);
        this.name = "MalformedEnvelopeError";
    }
}
export class UnsupportedEnvelopeError extends Error {
    version;
    algorithm;
    constructor(version, algorithm, reason) {
        super(`${reason} (версія ${version}, алгоритм ${algorithm})`);
        this.name = "UnsupportedEnvelopeError";
        this.version = version;
        this.algorithm = algorithm;
    }
}
export function nonceLength(algorithm) {
    return NONCE_BYTES[algorithm];
}
export function isKnownAlgorithm(value) {
    return (value === Algorithm.XChaCha20Poly1305 ||
        value === Algorithm.X25519Capture ||
        value === Algorithm.AesGcm256);
}
export function encodeEnvelope(input) {
    const version = input.version ?? SCHEMA_VERSION;
    const expected = nonceLength(input.algorithm);
    if (input.nonce.length !== expected) {
        throw new MalformedEnvelopeError(`нонс має бути ${expected} байтів, отримано ${input.nonce.length}`);
    }
    if (input.ciphertext.length === 0) {
        throw new MalformedEnvelopeError("шифротекст порожній");
    }
    const out = new Uint8Array(HEADER_BYTES + expected + input.ciphertext.length);
    out[0] = version;
    out[1] = input.algorithm;
    out.set(input.nonce, HEADER_BYTES);
    out.set(input.ciphertext, HEADER_BYTES + expected);
    return out;
}
/**
 * Розбирає конверт. Кидає `UnsupportedEnvelopeError` на незнайомій версії
 * чи алгоритмі — саме заради цієї помилки конверт і потрібен: блоб із
 * майбутньої версії має відмовити явно, а не мовчки не розшифруватися.
 */
export function decodeEnvelope(bytes) {
    if (bytes.length < HEADER_BYTES + 1) {
        throw new MalformedEnvelopeError(`замало байтів: ${bytes.length}`);
    }
    const version = bytes[0];
    const algorithm = bytes[1];
    if (version > SCHEMA_VERSION) {
        throw new UnsupportedEnvelopeError(version, algorithm, "блоб записаний новішою версією застосунку");
    }
    if (!isKnownAlgorithm(algorithm)) {
        throw new UnsupportedEnvelopeError(version, algorithm, "невідомий алгоритм");
    }
    const nonceBytes = nonceLength(algorithm);
    const body = HEADER_BYTES + nonceBytes;
    if (bytes.length <= body) {
        throw new MalformedEnvelopeError("немає шифротексту після заголовка");
    }
    return {
        version,
        algorithm,
        nonce: bytes.subarray(HEADER_BYTES, body),
        ciphertext: bytes.subarray(body),
    };
}
//# sourceMappingURL=envelope.js.map